<!-- src/components/common/AppContent.vue -->
<!--
  Zone défilante d'une page, à la place d'`ion-content`.

  La structure reprend celle d'`ion-content` à l'identique, et ce n'est pas de
  la nostalgie : c'est ce qui permet à AppRefresher de garder son algorithme.
  Le fond et la zone défilante sont deux calques SÉPARÉS posés en `inset: 0`,
  qu'on translate ensemble pendant la traction, pendant que l'indicateur reste
  immobile derrière eux.

      .app-content            hôte, relatif, prend la place restante
        .app-content-bg       le fond, non défilant
        <slot name="fixed">   ce qui ne défile pas (l'indicateur)
        .app-content-scroll   le contenu

  Une simplification par rapport à Ionic : plus de `--offset-top`. Le fond
  couvre toujours exactement l'hôte, donc le cas « plein écran » qui décalait
  le fond sous l'en-tête n'existe plus. La prop `fullscreen` est conservée
  parce que 46 appelants la passent, mais elle ne pilote plus rien : ces pages
  n'ont jamais eu d'`ion-header`, l'offset valait déjà zéro.
-->
<template>
  <div class="app-content" :class="{ 'app-content--no-scroll': !scrollY }">
    <div class="app-content-bg" aria-hidden="true" />
    <div ref="scrollRef" class="app-content-scroll" @scroll="onScroll">
      <slot />
    </div>
    <!-- ⚠️ APRÈS la zone défilante, et c'est tout ce qui le tient au-dessus.
         Les deux sont en `position: absolute` sans `z-index` : ils se peignent
         donc dans l'ordre du DOM, et le défilant, venant en second, recouvrait
         ce qui est censé rester épinglé. Le bandeau de la chouette de la page
         Habitudes disparaissait ainsi derrière le contenu, et la barre d'action
         de TemplateBatchSheet ne s'en sortait qu'avec un `z-10` posé chez elle,
         contournement local d'un défaut global.
         Pas d'enveloppe autour du slot : elle deviendrait le bloc conteneur de
         ses occupants, et la rendre transparente aux clics
         (`pointer-events: none`) casserait ceux qui ne rétablissent pas
         `auto` sur leurs zones tapables. -->
    <slot name="fixed" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

withDefaults(
  defineProps<{
    /** Conservé pour compatibilité d'appel ; sans `ion-header`, il ne pilotait déjà rien. */
    fullscreen?: boolean
    /** Défilement vertical actif. */
    scrollY?: boolean
  }>(),
  { fullscreen: false, scrollY: true },
)

/**
 * Le second argument est le scrollTop MAXIMAL de la zone. Il sert à
 * reconnaître le bas de page, que `scrollTop` seul ne permet pas de situer :
 * cf. `useScrollScrim`, où le rebond élastique d'iOS se faisait passer pour un
 * geste vers le haut. Les appelants qui n'en ont pas besoin l'ignorent.
 */
const emit = defineEmits<{ (e: 'scroll', scrollTop: number, maxScrollTop: number): void }>()

const scrollRef = ref<HTMLElement | null>(null)

function onScroll() {
  const el = scrollRef.value
  if (el) emit('scroll', el.scrollTop, Math.max(0, el.scrollHeight - el.clientHeight))
}

/**
 * Rendue en promesse, comme chez Ionic : AppRefresher l'attend, et l'élément
 * peut ne pas être monté au moment de l'appel.
 */
function getScrollElement(): Promise<HTMLElement | null> {
  return Promise.resolve(scrollRef.value)
}

function scrollToTop(duration = 0) {
  scrollRef.value?.scrollTo({ top: 0, behavior: duration > 0 ? 'smooth' : 'auto' })
}

function scrollToBottom(duration = 0) {
  const el = scrollRef.value
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: duration > 0 ? 'smooth' : 'auto' })
}

defineExpose({ getScrollElement, scrollToTop, scrollToBottom })
</script>

<style>
.app-content {
  position: relative;
  display: block;
  flex: 1 1 0;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  color: var(--color-ink);
}

/* Fond des pages : dégradé subtil blanc en haut vers crème Soleil en bas.
   Calque à part plutôt que fond de la zone défilante : il doit rester fixe
   quand le contenu défile, et suivre la traction du tirer-pour-rafraîchir. */
.app-content-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, #ffffff 0%, var(--color-warm) 60%);
}

.app-content-scroll {
  position: absolute;
  /* `--app-content-bottom` : une page qui épingle un objet en bas (la scène
     des Tâches de l'enfant) arrête le défilement au-dessus de lui, au lieu de
     faire passer son contenu dessous. */
  inset: 0 0 var(--app-content-bottom, 0px) 0;
  /* `0` et non `auto` : il CRÉE un contexte d'empilement, ce qui enferme les
     z-index du contenu de page. Sans lui, un `z-50` posé dans une carte
     repasserait par-dessus ce qui est épinglé. Et `0` reste dans la même
     couche de peinture que le slot `fixed` juste après, où l'ordre du DOM
     tranche : mettre `1` ici le ferait au contraire repasser devant. */
  z-index: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Le rebond reste au conteneur : sans ça, tirer en bas de liste fait bouger
     toute la page sous la WebView. */
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  /* Laisse passer le double-tap-zoom sans les 300ms d'attente. */
  touch-action: manipulation;
}

.app-content--no-scroll .app-content-scroll {
  overflow-y: hidden;
}

/* Les pages à héros laissent voir leur illustration sous l'en-tête. */
.app-content.content-bg-transparent .app-content-bg {
  background: transparent;
}

/* Sur grand écran, la pièce est simplement crème. Le dégradé blanc vers crème
   est une finesse de téléphone : sur une colonne mesurée il ne se lit plus, et
   il trahit la colonne. Une page qui repeint du crème plat dans sa largeur
   (les écrans d'accueil et d'onboarding le font) faisait apparaître une
   couture verticale nette entre sa colonne et le dégradé des bords.
   Cette règle vivait dans index.css, sous `@layer base` : ce style-ci, hors
   couche, la battait et le dégradé restait. */
@media (min-width: 768px) {
  .app-content:not(.content-bg-transparent) .app-content-bg {
    background: var(--color-warm);
  }
}
</style>
